import {ErrorResponse} from '@/domain';

const ERROR_USER_NOT_REGISTERED = 'e_user_not_registered';
const ERROR_USER_ALREADY_REGISTERED = 'e_user_already_registered';
const ERROR_USER_USING_VPN = 'e_vpn';
const ERROR_USER_BANNED = 'e_banned';
const ERROR_BALANCE_SMASHES_LOW = 'e_balance_smashes_low';
const ERROR_BALANCE_SUPER_SMASHES_LOW = 'e_balance_super_smashes_low';
const ERROR_EMAIL_EMPTY = 'e_null_email';
const ERROR_OAUTH_TOKEN_EXPIRED = 'e_invalid_id_token';
const ERROR_PROTECT_MESSAGE = 'e_protect_message';
const ERROR_PROTECT_MESSAGE_IMAGE = 'e_protect_message_image';
const ERROR_SENSITIVE_MESSAGE_IMAGE = 'e_sensitive_message_image';
const ERROR_VERIFICATION_KEYWORD_NOT_ALLOWED =
  'e_verification_keyword_not_allowed';
const ERROR_VERIFICATION_SELF_REQUIRED = 'e_verification_self_required';
const ERROR_USER_ALREADY_VERIFIED = 'e_user_already_verified';
const ERROR_LOCATION_USER_ALREADY_VERIFIED = 'e_location_user_already_verified';
const ERROR_SIGN_RATE_LIMIT = 'e_sign_rate_limit';
const ERROR_PHONE_VERIFICATION = 'e_phone_verification';
const ERROR_INVALID_CODE = 'e_invalid_code';
const ERROR_INVALID_PHONE = 'e_invalid_phone';
const ERROR_PHONE_LIMIT_REACHED = 'e_phone_limit_reached';
const ERROR_ALREADY_CONFIRMED = 'e_already_confirmed';
const ERROR_INTERNAL = 'e_internal';
const ERROR_TOKEN_NOT_FOUND = 'e_token_not_found';
const ERROR_EMAIL_LIMIT_REACHED = 'e_email_limit_reached';
const ERROR_INVALID_PROMPT = 'e_invalid_prompt_text';
const ERROR_INVALID_EMAIL = 'e_invalid_email';
const ERROR_EMAIL_VERIFICATION = 'e_email_verification';
const ERROR_MATCH_FAVOURITE_LAST_NOT_FOUND = 'e_match_favourite_last_not_found';
const ERROR_CASH_GATEWAY = 'e_cash_gateway_error';

type AnyFunction = (...args: any[]) => void;

abstract class BaseCase<T extends AnyFunction = (...args: any[]) => void> {
  abstract errorMessage: string | null;

  constructor(protected applyFunction: T) {}

  protected runFunction(_: ErrorResponse) {
    this.applyFunction();
  }

  handle(error: ErrorResponse, next: () => void) {
    // Tap case
    if (this.errorMessage === null) {
      this.runFunction(error);
      next();
      return;
    }

    if (error.m === this.errorMessage) {
      this.runFunction(error);
      return;
    }

    next();
  }
}

/**
 * Run handled as default
 */
export class TapCase extends BaseCase {
  errorMessage = null;
}

export class UserAlreadyRegisteredCase extends BaseCase {
  errorMessage = ERROR_USER_ALREADY_REGISTERED;
}

export class UserNotRegisteredCase extends BaseCase {
  errorMessage = ERROR_USER_NOT_REGISTERED;
}

export class UserUsingVPNCase extends BaseCase {
  errorMessage = ERROR_USER_USING_VPN;
}

export class BalanceSmashesLowCase extends BaseCase {
  errorMessage = ERROR_BALANCE_SMASHES_LOW;
}

export class BalanceSuperSmashesLowCase extends BaseCase {
  errorMessage = ERROR_BALANCE_SUPER_SMASHES_LOW;
}

export class EmptyEmailCase extends BaseCase {
  errorMessage = ERROR_EMAIL_EMPTY;
}

export class OauthTokenExpiredCase extends BaseCase {
  errorMessage = ERROR_OAUTH_TOKEN_EXPIRED;
}

export class ProtectMessageCase extends BaseCase {
  errorMessage = ERROR_PROTECT_MESSAGE;
}

export class ProtectMessageImageCase extends BaseCase {
  errorMessage = ERROR_PROTECT_MESSAGE_IMAGE;
}

export class SensitiveMessageImageCase extends BaseCase {
  errorMessage = ERROR_SENSITIVE_MESSAGE_IMAGE;
}

export class VerificationKeywordNotAllowedCase extends BaseCase {
  errorMessage = ERROR_VERIFICATION_KEYWORD_NOT_ALLOWED;
}

export class VerificationSelfRequiredCase extends BaseCase {
  errorMessage = ERROR_VERIFICATION_SELF_REQUIRED;
}

export class UserAlreadyVerifiedCase extends BaseCase {
  errorMessage = ERROR_USER_ALREADY_VERIFIED;
}

export class LocationUserAlreadyVerifiedCase extends BaseCase {
  errorMessage = ERROR_LOCATION_USER_ALREADY_VERIFIED;
}

export class SignRateLimitCase extends BaseCase {
  errorMessage = ERROR_SIGN_RATE_LIMIT;
}

export class PhoneVerificationCase extends BaseCase {
  errorMessage = ERROR_PHONE_VERIFICATION;
}

export class InvalidCodeCase extends BaseCase {
  errorMessage = ERROR_INVALID_CODE;
}

export class InvalidPhoneCase extends BaseCase {
  errorMessage = ERROR_INVALID_PHONE;
}

export class PhoneLimitReachedCase extends BaseCase {
  errorMessage = ERROR_PHONE_LIMIT_REACHED;
}

export class AlreadyConfirmedCase extends BaseCase {
  errorMessage = ERROR_ALREADY_CONFIRMED;
}

export class InternalCase extends BaseCase {
  errorMessage = ERROR_INTERNAL;
}

export class TokenNotFoundCase extends BaseCase {
  errorMessage = ERROR_TOKEN_NOT_FOUND;
}

export class EmailLimitReachedCase extends BaseCase {
  errorMessage = ERROR_EMAIL_LIMIT_REACHED;
}

export class InvalidPromptCase extends BaseCase {
  errorMessage = ERROR_INVALID_PROMPT;
}

export class InvalidEmailCase extends BaseCase {
  errorMessage = ERROR_INVALID_EMAIL;
}

export class EmailVerificationCase extends BaseCase {
  errorMessage = ERROR_EMAIL_VERIFICATION;
}

export class MatchFavouriteLastNotFoundCase extends BaseCase {
  errorMessage = ERROR_MATCH_FAVOURITE_LAST_NOT_FOUND;
}

export class CashGatewayErrorCase extends BaseCase {
  errorMessage = ERROR_CASH_GATEWAY;
}

type BannedError = Extract<ErrorResponse, {m: 'e_banned'}>;

export class UserBannedCase extends BaseCase<
  (info: BannedError['info']) => void
> {
  errorMessage = ERROR_USER_BANNED;

  protected runFunction(error: BannedError) {
    this.applyFunction(error.info);
  }
}

class ErrorHandlerItem {
  next?: ErrorHandlerItem;
  constructor(public current: BaseCase) {}
}

export class ErrorHandler {
  private firstCase: ErrorHandlerItem;
  private currentCase: ErrorHandlerItem;

  constructor() {
    this.firstCase = new ErrorHandlerItem(
      new TapCase(() => {
        // No operation
      })
    );
    this.currentCase = this.firstCase;
  }

  use(useCase: BaseCase | BaseCase[]) {
    if (Array.isArray(useCase)) {
      useCase.forEach((item) => {
        this.addUseCase(item);
      });
      return this;
    }

    this.addUseCase(useCase);
    return this;
  }

  handle(error: ErrorResponse) {
    this.run(error, this.firstCase.next);
  }

  private addUseCase(useCase: BaseCase) {
    this.currentCase.next = new ErrorHandlerItem(useCase);
    this.currentCase = this.currentCase.next;
  }

  private run(error: ErrorResponse, baseCase?: ErrorHandlerItem) {
    if (!baseCase) {
      return;
    }

    baseCase.current.handle(error, () => this.run(error, baseCase.next));
  }
}
